import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, 
  Bot, 
  Settings, 
  Save, 
  RefreshCw,
  Eye, 
  EyeOff,
  Globe,
  Home,
  Users,
  ShoppingBag,
  FileText,
  Calendar,
  Calculator,
  MessageCircle,
  Shield,
  Database,
  Mail,
  Bell,
  Zap,
  CheckCircle,
  XCircle,
  Plus,
  Trash2,
  Edit,
  Copy,
  Search,
  Filter,
  Download,
  Upload,
  MoreHorizontal,
  ChevronDown,
  ChevronRight,
  BarChart3,
  Activity,
  Brain
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { adminAPI } from '../../services/api';

const FloatingChatManagement = () => {
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [showABTesting, setShowABTesting] = useState(false);
  const [settings, setSettings] = useState({
    whatsapp_enabled: true,
    ai_chat_enabled: true,
    whatsapp_button_text: 'WhatsApp Support',
    ai_button_text: 'AI Assistant',
    whatsapp_button_color: '#25D366',
    ai_button_color: '#667eea',
    position: 'bottom-right',
    z_index: 50,
    show_on_mobile: true,
    show_on_desktop: true,
    auto_hide: false,
    auto_hide_delay: 5000,
    // Advanced Features
    scheduled_visibility: {
      enabled: false,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      rules: [
        {
          id: 1,
          name: 'Business Hours',
          enabled: true,
          days: [1, 2, 3, 4, 5], // Monday to Friday
          start_time: '09:00',
          end_time: '18:00',
          action: 'show' // show, hide, or custom
        }
      ]
    },
    advanced_targeting: {
      enabled: false,
      user_roles: ['all'], // all, guest, user, admin
      user_locations: [], // country codes
      user_devices: ['all'], // all, mobile, desktop, tablet
      user_browsers: ['all'], // all, chrome, firefox, safari, edge
      user_behavior: {
        min_time_on_page: 0, // seconds
        min_scroll_percentage: 0, // percentage
        min_clicks: 0
      }
    },
    performance_settings: {
      lazy_loading: true,
      animation_duration: 300, // milliseconds
      hover_effects: true,
      sound_effects: false,
      accessibility_mode: false
    },
    analytics: {
      enabled: true,
      track_clicks: true,
      track_views: true,
      track_conversions: true,
      heatmap_tracking: false
    },
    ab_testing: {
      enabled: false,
      tests: [
        {
          id: 1,
          name: 'Button Position Test',
          description: 'Test different button positions for engagement',
          status: 'draft', // draft, running, paused, completed
          variants: [
            {
              id: 'control',
              name: 'Control (Bottom Right)',
              settings: {
                position: 'bottom-right',
                whatsapp_button_color: '#25D366',
                ai_button_color: '#667eea'
              },
              traffic_percentage: 50
            },
            {
              id: 'variant_a',
              name: 'Variant A (Top Right)',
              settings: {
                position: 'top-right',
                whatsapp_button_color: '#FF6B6B',
                ai_button_color: '#4ECDC4'
              },
              traffic_percentage: 50
            }
          ],
          metrics: ['click_rate', 'conversion_rate', 'time_to_click'],
          start_date: null,
          end_date: null,
          results: {
            control: { impressions: 0, clicks: 0, conversions: 0 },
            variant_a: { impressions: 0, clicks: 0, conversions: 0 }
          }
        }
      ]
    },
    page_visibility: {
      home: true,
      about: true,
      services: true,
      products: true,
      portfolio: true,
      blog: true,
      contact: true,
      quote: true,
      appointment: true,
      cart: true,
      checkout: true,
      dashboard: false,
      admin: false
    }
  });

  const [pageGroups, setPageGroups] = useState([
    {
      id: 'main-pages',
      label: 'Main Website Pages',
      icon: Globe,
      pages: [
        { id: 'home', label: 'Home Page', icon: Home, description: 'Main landing page' },
        { id: 'about', label: 'About Us', icon: Users, description: 'Company information page' },
        { id: 'services', label: 'Services', icon: Settings, description: 'Services listing page' },
        { id: 'products', label: 'Products', icon: ShoppingBag, description: 'Products catalog page' },
        { id: 'portfolio', label: 'Portfolio', icon: Eye, description: 'Work showcase page' },
        { id: 'blog', label: 'Blog', icon: FileText, description: 'Blog articles page' },
        { id: 'contact', label: 'Contact', icon: MessageCircle, description: 'Contact information page' },
        { id: 'quote', label: 'Quote Request', icon: Calculator, description: 'Quote form page' },
        { id: 'appointment', label: 'Appointment', icon: Calendar, description: 'Appointment booking page' }
      ]
    },
    {
      id: 'ecommerce-pages',
      label: 'E-commerce Pages',
      icon: ShoppingBag,
      pages: [
        { id: 'cart', label: 'Shopping Cart', icon: ShoppingBag, description: 'Cart page' },
        { id: 'checkout', label: 'Checkout', icon: ShoppingBag, description: 'Checkout process page' }
      ]
    },
    {
      id: 'user-pages',
      label: 'User & Admin Pages',
      icon: Shield,
      pages: [
        { id: 'dashboard', label: 'User Dashboard', icon: Database, description: 'User account dashboard' },
        { id: 'admin', label: 'Admin Panel', icon: Shield, description: 'Administrative interface' }
      ]
    }
  ]);

  const [selectedPages, setSelectedPages] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getFloatingChatSettings();
      console.log('🔍 Backend response:', response.data);
      
      if (response.data?.success) {
        const backendSettings = response.data.settings || {};
        console.log('🔍 Backend settings received:', backendSettings);
        
        // Only apply defaults for missing properties, don't override existing ones
        const mergedSettings = {
          // Basic settings with defaults as fallback
          whatsapp_enabled: backendSettings.whatsapp_enabled ?? true,
          whatsapp_chat_mode: backendSettings.whatsapp_chat_mode ?? 'web_chat',
          whatsapp_direct_number: backendSettings.whatsapp_direct_number ?? '+919515377622',
          whatsapp_direct_message_template: backendSettings.whatsapp_direct_message_template ?? 'Hi {name}',
          ai_chat_enabled: backendSettings.ai_chat_enabled ?? true,
          whatsapp_button_text: backendSettings.whatsapp_button_text ?? 'WhatsApp Support',
          ai_button_text: backendSettings.ai_button_text ?? 'AI Assistant',
          whatsapp_button_color: backendSettings.whatsapp_button_color ?? '#25D366',
          ai_button_color: backendSettings.ai_button_color ?? '#667eea',
          position: backendSettings.position ?? 'bottom-right',
          z_index: backendSettings.z_index ?? 50,
          show_on_mobile: backendSettings.show_on_mobile ?? true,
          show_on_desktop: backendSettings.show_on_desktop ?? true,
          auto_hide: backendSettings.auto_hide ?? false,
          auto_hide_delay: backendSettings.auto_hide_delay ?? 5000,
          
          // Advanced Features - use backend settings if they exist, otherwise use defaults
          scheduled_visibility: backendSettings.scheduled_visibility ?? {
            enabled: false,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            rules: [
              {
                id: 1,
                name: 'Business Hours',
                enabled: true,
                days: [1, 2, 3, 4, 5],
                start_time: '09:00',
                end_time: '18:00',
                action: 'show'
              }
            ]
          },
          advanced_targeting: backendSettings.advanced_targeting ?? {
            enabled: false,
            user_roles: ['all'],
            user_locations: [],
            user_devices: ['all'],
            user_browsers: ['all'],
            user_behavior: {
              min_time_on_page: 0,
              min_scroll_percentage: 0,
              min_clicks: 0
            }
          },
          performance_settings: backendSettings.performance_settings ?? {
            lazy_loading: true,
            animation_duration: 300,
            hover_effects: true,
            sound_effects: false,
            accessibility_mode: false
          },
          analytics: backendSettings.analytics ?? {
            enabled: true,
            track_clicks: true,
            track_views: true,
            track_conversions: true,
            heatmap_tracking: false
          },
          ab_testing: backendSettings.ab_testing ?? {
            enabled: false,
            tests: [
              {
                id: 1,
                name: 'Button Position Test',
                description: 'Test different button positions for engagement',
                status: 'draft',
                variants: [
                  {
                    id: 'control',
                    name: 'Control (Bottom Right)',
                    settings: {
                      position: 'bottom-right',
                      whatsapp_button_color: '#25D366',
                      ai_button_color: '#667eea'
                    },
                    traffic_percentage: 50
                  },
                  {
                    id: 'variant_a',
                    name: 'Variant A (Top Right)',
                    settings: {
                      position: 'top-right',
                      whatsapp_button_color: '#FF6B6B',
                      ai_button_color: '#4ECDC4'
                    },
                    traffic_percentage: 50
                  }
                ],
                metrics: ['click_rate', 'conversion_rate', 'time_to_click'],
                start_date: null,
                end_date: null,
                results: {
                  control: { impressions: 0, clicks: 0, conversions: 0 },
                  variant_a: { impressions: 0, clicks: 0, conversions: 0 }
                }
              }
            ]
          },
          page_visibility: backendSettings.page_visibility ?? {
            home: true,
            about: true,
            services: true,
            products: true,
            portfolio: true,
            blog: true,
            contact: true,
            quote: true,
            appointment: true,
            cart: true,
            checkout: true,
            dashboard: false,
            admin: false
          }
        };
        
        setSettings(mergedSettings);
      }
    } catch (error) {
      console.error('Error loading floating chat settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaveLoading(true);
      console.log('🔍 Saving settings:', settings);
      
      const response = await adminAPI.updateFloatingChatSettings(settings);
      console.log('🔍 Save response:', response.data);
      
      if (response.data?.success) {
        toast.success('Settings saved successfully!');
        // Reload settings to confirm they were saved
        await loadSettings();
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving floating chat settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handlePageVisibilityChange = (pageId, value) => {
    setSettings(prev => ({
      ...prev,
      page_visibility: {
        ...prev.page_visibility,
        [pageId]: value
      }
    }));
  };

  const handleBulkPageAction = (action) => {
    if (selectedPages.length === 0) {
      toast.error('Please select pages first');
      return;
    }

    const newPageVisibility = { ...settings.page_visibility };
    
    selectedPages.forEach(pageId => {
      switch (action) {
        case 'enable':
          newPageVisibility[pageId] = true;
          break;
        case 'disable':
          newPageVisibility[pageId] = false;
          break;
        case 'toggle':
          newPageVisibility[pageId] = !newPageVisibility[pageId];
          break;
        default:
          break;
      }
    });

    setSettings(prev => ({
      ...prev,
      page_visibility: newPageVisibility
    }));

    setSelectedPages([]);
    toast.success(`Bulk action "${action}" applied to ${selectedPages.length} pages`);
  };

  const handleSelectAllPages = (groupId) => {
    const group = pageGroups.find(g => g.id === groupId);
    if (group) {
      const allPageIds = group.pages.map(p => p.id);
      setSelectedPages(prev => {
        const newSelected = [...prev];
        allPageIds.forEach(pageId => {
          if (!newSelected.includes(pageId)) {
            newSelected.push(pageId);
          }
        });
        return newSelected;
      });
    }
  };

  const handleDeselectAllPages = (groupId) => {
    const group = pageGroups.find(g => g.id === groupId);
    if (group) {
      const allPageIds = group.pages.map(p => p.id);
      setSelectedPages(prev => prev.filter(pageId => !allPageIds.includes(pageId)));
    }
  };

  const filteredPageGroups = pageGroups.map(group => ({
    ...group,
    pages: group.pages.filter(page => 
      page.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      page.description.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(group => group.pages.length > 0);

  const resetToDefaults = () => {
    if (window.confirm('Are you sure you want to reset all settings to defaults?')) {
      setSettings({
        whatsapp_enabled: true,
        ai_chat_enabled: true,
        whatsapp_button_text: 'WhatsApp Support',
        ai_button_text: 'AI Assistant',
        whatsapp_button_color: '#25D366',
        ai_button_color: '#667eea',
        position: 'bottom-right',
        z_index: 50,
        show_on_mobile: true,
        show_on_desktop: true,
        auto_hide: false,
        auto_hide_delay: 5000,
        // Advanced Features
        scheduled_visibility: {
          enabled: false,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          rules: [
            {
              id: 1,
              name: 'Business Hours',
              enabled: true,
              days: [1, 2, 3, 4, 5], // Monday to Friday
              start_time: '09:00',
              end_time: '18:00',
              action: 'show' // show, hide, or custom
            }
          ]
        },
        advanced_targeting: {
          enabled: false,
          user_roles: ['all'], // all, guest, user, admin
          user_locations: [], // country codes
          user_devices: ['all'], // all, mobile, desktop, tablet
          user_browsers: ['all'], // all, chrome, firefox, safari, edge
          user_behavior: {
            min_time_on_page: 0, // seconds
            min_scroll_percentage: 0, // percentage
            min_clicks: 0
          }
        },
        performance_settings: {
          lazy_loading: true,
          animation_duration: 300, // milliseconds
          hover_effects: true,
          sound_effects: false,
          accessibility_mode: false
        },
        analytics: {
          enabled: true,
          track_clicks: true,
          track_views: true,
          track_conversions: true,
          heatmap_tracking: false
        },
        ab_testing: {
          enabled: false,
          tests: [
            {
              id: 1,
              name: 'Button Position Test',
              description: 'Test different button positions for engagement',
              status: 'draft', // draft, running, paused, completed
              variants: [
                {
                  id: 'control',
                  name: 'Control (Bottom Right)',
                  settings: {
                    position: 'bottom-right',
                    whatsapp_button_color: '#25D366',
                    ai_button_color: '#667eea'
                  },
                  traffic_percentage: 50
                },
                {
                  id: 'variant_a',
                  name: 'Variant A (Top Right)',
                  settings: {
                    position: 'top-right',
                    whatsapp_button_color: '#FF6B6B',
                    ai_button_color: '#4ECDC4'
                  },
                  traffic_percentage: 50
                }
              ],
              metrics: ['click_rate', 'conversion_rate', 'time_to_click'],
              start_date: null,
              end_date: null,
              results: {
                control: { impressions: 0, clicks: 0, conversions: 0 },
                variant_a: { impressions: 0, clicks: 0, conversions: 0 }
              }
            }
          ]
        },
        page_visibility: {
          home: true,
          about: true,
          services: true,
          products: true,
          portfolio: true,
          blog: true,
          contact: true,
          quote: true,
          appointment: true,
          cart: true,
          checkout: true,
          dashboard: false,
          admin: false
        }
      });
      toast.success('Settings reset to defaults');
    }
  };



  const exportSettings = () => {
    const dataStr = JSON.stringify(settings, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'floating-chat-settings.json';
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Settings exported successfully');
  };

  const importSettings = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedSettings = JSON.parse(e.target.result);
          setSettings(importedSettings);
          toast.success('Settings imported successfully');
        } catch (error) {
          toast.error('Invalid settings file');
        }
      };
      reader.readAsText(file);
    }
  };

  const quickPreview = () => {
    const previewWindow = window.open('', '_blank');
    const previewHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Floating Chat Preview</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .preview-info { background: #f0f0f0; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
            .preview-buttons { position: fixed; bottom: 20px; right: 20px; z-index: ${settings.z_index}; }
            .preview-button { 
              width: 60px; height: 60px; border-radius: 50%; margin: 10px; 
              display: flex; align-items: center; justify-content: center; 
              color: white; font-weight: bold; cursor: pointer; box-shadow: 0 4px 12px rgba(0,0,0,0.3);
            }
            .whatsapp-btn { background-color: ${settings.whatsapp_button_color}; }
            .ai-btn { background-color: ${settings.ai_button_color}; }
          </style>
        </head>
        <body>
          <div class="preview-info">
            <h2>Floating Chat Preview</h2>
            <p><strong>Position:</strong> ${settings.position}</p>
            <p><strong>Z-Index:</strong> ${settings.z_index}</p>
            <p><strong>WhatsApp:</strong> ${settings.whatsapp_enabled ? 'Enabled' : 'Disabled'}</p>
            <p><strong>AI Chat:</strong> ${settings.ai_chat_enabled ? 'Enabled' : 'Disabled'}</p>
          </div>
          <div class="preview-buttons" style="
            ${settings.position === 'bottom-right' ? 'bottom: 20px; right: 20px;' : ''}
            ${settings.position === 'bottom-left' ? 'bottom: 20px; left: 20px;' : ''}
            ${settings.position === 'top-right' ? 'top: 20px; right: 20px;' : ''}
            ${settings.position === 'top-left' ? 'top: 20px; left: 20px;' : ''}
          ">
            ${settings.whatsapp_enabled ? `<div class="preview-button whatsapp-btn">💬</div>` : ''}
            ${settings.ai_chat_enabled ? `<div class="preview-button ai-btn">🤖</div>` : ''}
          </div>
        </body>
      </html>
    `;
    previewWindow.document.write(previewHTML);
    previewWindow.document.close();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading floating chat settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
                <MessageSquare className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 text-blue-600" />
                Floating Chat Management
              </h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-2">
                Control the visibility and behavior of WhatsApp and AI chat buttons across your website
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
              <button
                onClick={quickPreview}
                className="px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-purple-100 dark:bg-purple-900 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-800 transition-colors flex items-center justify-center"
              >
                <Eye className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Preview</span>
                <span className="sm:hidden">Preview</span>
              </button>
              <button
                onClick={exportSettings}
                className="px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-green-100 dark:bg-green-900 rounded-lg hover:bg-green-200 dark:hover:bg-green-800 transition-colors flex items-center justify-center"
              >
                <Download className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Export</span>
                <span className="sm:hidden">Export</span>
              </button>
              <label className="px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-blue-100 dark:bg-blue-900 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors flex items-center justify-center cursor-pointer">
                <Upload className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Import</span>
                <span className="sm:hidden">Import</span>
                <input
                  type="file"
                  accept=".json"
                  onChange={importSettings}
                  className="hidden"
                />
              </label>

              <button
                onClick={resetToDefaults}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Reset to Defaults
              </button>
              <button
                onClick={saveSettings}
                disabled={saveLoading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {saveLoading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Settings
              </button>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <MessageSquare className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">WhatsApp Status</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {settings.whatsapp_enabled ? '✅ Enabled' : '❌ Disabled'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Bot className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">AI Chat Status</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {settings.ai_chat_enabled ? '✅ Enabled' : '❌ Disabled'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <Globe className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Visible Pages</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {Object.values(settings.page_visibility).filter(Boolean).length} / {Object.keys(settings.page_visibility).length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                <Settings className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Position</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white capitalize">
                  {settings.position.replace('-', ' ')}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:p-6">
          {/* Main Settings */}
          <div className="lg:col-span-2 space-y-6">
            {/* Button Configuration */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <Settings className="w-5 h-5 mr-2 text-blue-600" />
                Button Configuration
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
                {/* WhatsApp Button Settings */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="whatsapp_enabled"
                      checked={settings.whatsapp_enabled}
                      onChange={(e) => handleSettingChange('whatsapp_enabled', e.target.checked)}
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                    />
                    <label htmlFor="whatsapp_enabled" className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                      <MessageSquare className="w-4 h-4 mr-2 text-green-600" />
                      Enable WhatsApp Chat Button
                    </label>
                  </div>
                  
                  {settings.whatsapp_enabled && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Button Text
                        </label>
                        <input
                          type="text"
                          value={settings.whatsapp_button_text}
                          onChange={(e) => handleSettingChange('whatsapp_button_text', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="WhatsApp Support"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Button Color
                        </label>
                        <input
                          type="color"
                          value={settings.whatsapp_button_color}
                          onChange={(e) => handleSettingChange('whatsapp_button_color', e.target.value)}
                          className="w-full h-10 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
                        />
                      </div>
                      
                      {/* WhatsApp Chat Mode Settings */}
                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Chat Mode
                        </label>
                        <div className="space-y-2">
                          <label className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name="whatsapp_chat_mode"
                              value="web_chat"
                              checked={settings.whatsapp_chat_mode === 'web_chat'}
                              onChange={(e) => handleSettingChange('whatsapp_chat_mode', e.target.value)}
                              className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 focus:ring-green-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Web Chat</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">(Chat inside website)</span>
                          </label>
                          
                          <label className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name="whatsapp_chat_mode"
                              value="direct_chat"
                              checked={settings.whatsapp_chat_mode === 'direct_chat'}
                              onChange={(e) => handleSettingChange('whatsapp_chat_mode', e.target.value)}
                              className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 focus:ring-green-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Direct Chat</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">(Open WhatsApp directly)</span>
                          </label>
                        </div>
                      </div>
                      
                      {/* Direct Chat Settings */}
                      {settings.whatsapp_chat_mode === 'direct_chat' && (
                        <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              WhatsApp Number
                            </label>
                            <input
                              type="text"
                              value={settings.whatsapp_direct_number}
                              onChange={(e) => handleSettingChange('whatsapp_direct_number', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              placeholder="+919515377622"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Include country code (e.g., +91)
                            </p>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Message Template
                            </label>
                            <input
                              type="text"
                              value={settings.whatsapp_direct_message_template}
                              onChange={(e) => handleSettingChange('whatsapp_direct_message_template', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              placeholder="Hi {name}"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Use {"{name}"} to include the user's name
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* AI Chat Button Settings */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="ai_chat_enabled"
                      checked={settings.ai_chat_enabled}
                      onChange={(e) => handleSettingChange('ai_chat_enabled', e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="ai_chat_enabled" className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                      <Bot className="w-4 h-4 mr-2 text-blue-600" />
                      Enable AI Chat Button
                    </label>
                  </div>
                  
                  {settings.ai_chat_enabled && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Button Text
                        </label>
                        <input
                          type="text"
                          value={settings.ai_button_text}
                          onChange={(e) => handleSettingChange('ai_button_text', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="AI Assistant"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Button Color
                        </label>
                        <input
                          type="color"
                          value={settings.ai_button_color}
                          onChange={(e) => handleSettingChange('ai_button_color', e.target.value)}
                          className="w-full h-10 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Page Visibility Control */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <Eye className="w-5 h-5 mr-2 text-blue-600" />
                  Page Visibility Control
                </h2>
                
                {/* Search */}
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search pages..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Bulk Actions */}
              {selectedPages.length > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-5 h-5 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                        {selectedPages.length} page(s) selected
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleBulkPageAction('enable')}
                        className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 dark:bg-green-900 dark:text-green-200 rounded hover:bg-green-200 dark:hover:bg-green-800 transition-colors"
                      >
                        Enable All
                      </button>
                      <button
                        onClick={() => handleBulkPageAction('disable')}
                        className="px-3 py-1 text-xs font-medium text-red-700 bg-red-100 dark:bg-red-900 dark:text-red-200 rounded hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
                      >
                        Disable All
                      </button>
                      <button
                        onClick={() => handleBulkPageAction('toggle')}
                        className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 dark:bg-blue-900 dark:text-blue-200 rounded hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                      >
                        Toggle All
                      </button>
                      <button
                        onClick={() => setSelectedPages([])}
                        className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-200 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        Clear Selection
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Page Groups */}
              <div className="space-y-4">
                {filteredPageGroups.map((group) => (
                  <div key={group.id} className="border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-center space-x-2">
                        <group.icon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        <h3 className="font-medium text-gray-900 dark:text-white">{group.label}</h3>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleSelectAllPages(group.id)}
                          className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          Select All
                        </button>
                        <button
                          onClick={() => handleDeselectAllPages(group.id)}
                          className="text-xs text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                        >
                          Deselect All
                        </button>
                      </div>
                    </div>
                    
                    <div className="p-4 space-y-3">
                      {group.pages.map((page) => (
                        <div key={page.id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              id={`page_${page.id}`}
                              checked={selectedPages.includes(page.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedPages(prev => [...prev, page.id]);
                                } else {
                                  setSelectedPages(prev => prev.filter(id => id !== page.id));
                                }
                              }}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <div className="flex items-center space-x-2">
                              <page.icon className="w-4 h-4 text-gray-500" />
                              <div>
                                <label htmlFor={`page_${page.id}`} className="text-sm font-medium text-gray-900 dark:text-white cursor-pointer">
                                  {page.label}
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{page.description}</p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              settings.page_visibility[page.id]
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                              {settings.page_visibility[page.id] ? 'Visible' : 'Hidden'}
                            </span>
                            
                            <button
                              onClick={() => handlePageVisibilityChange(page.id, !settings.page_visibility[page.id])}
                              className={`p-1 rounded ${
                                settings.page_visibility[page.id]
                                  ? 'text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20'
                                  : 'text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20'
                              }`}
                            >
                              {settings.page_visibility[page.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar Settings */}
          <div className="space-y-6">
            {/* Display Settings */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <Settings className="w-5 h-5 mr-2 text-blue-600" />
                Display Settings
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Position
                  </label>
                  <select
                    value={settings.position}
                    onChange={(e) => handleSettingChange('position', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="bottom-right">Bottom Right</option>
                    <option value="bottom-left">Bottom Left</option>
                    <option value="top-right">Top Right</option>
                    <option value="top-left">Top Left</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Z-Index
                  </label>
                  <input
                    type="number"
                    value={settings.z_index}
                    onChange={(e) => handleSettingChange('z_index', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    min="1"
                    max="9999"
                  />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="show_on_mobile"
                      checked={settings.show_on_mobile}
                      onChange={(e) => handleSettingChange('show_on_mobile', e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="show_on_mobile" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Show on Mobile Devices
                    </label>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="show_on_desktop"
                      checked={settings.show_on_desktop}
                      onChange={(e) => handleSettingChange('show_on_desktop', e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="show_on_desktop" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Show on Desktop Devices
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Advanced Settings */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <Zap className="w-5 h-5 mr-2 text-blue-600" />
                  Advanced Settings
                </h2>
                <button
                  onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                  className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  {showAdvancedSettings ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
              </div>
              
              {showAdvancedSettings && (
                <div className="space-y-6">
                  {/* Auto-hide Settings */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="auto_hide"
                        checked={settings.auto_hide}
                        onChange={(e) => handleSettingChange('auto_hide', e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="auto_hide" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Auto-hide buttons
                      </label>
                    </div>
                    
                    {settings.auto_hide && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Auto-hide Delay (ms)
                        </label>
                        <input
                          type="number"
                          value={settings.auto_hide_delay}
                          onChange={(e) => handleSettingChange('auto_hide_delay', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          min="1000"
                          max="30000"
                          step="1000"
                        />
                      </div>
                    )}
                  </div>

                  {/* Scheduled Visibility */}
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-md font-medium text-gray-900 dark:text-white">Scheduled Visibility</h3>
                      <input
                        type="checkbox"
                        id="scheduled_visibility_enabled"
                        checked={settings.scheduled_visibility.enabled}
                        onChange={(e) => handleSettingChange('scheduled_visibility', {
                          ...settings.scheduled_visibility,
                          enabled: e.target.checked
                        })}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </div>
                    
                    {settings.scheduled_visibility.enabled && (
                      <div className="space-y-3 pl-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Timezone
                          </label>
                          <select
                            value={settings.scheduled_visibility.timezone}
                            onChange={(e) => handleSettingChange('scheduled_visibility', {
                              ...settings.scheduled_visibility,
                              timezone: e.target.value
                            })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            <option value="UTC">UTC</option>
                            <option value="America/New_York">Eastern Time</option>
                            <option value="America/Chicago">Central Time</option>
                            <option value="America/Denver">Mountain Time</option>
                            <option value="America/Los_Angeles">Pacific Time</option>
                            <option value="Europe/London">London</option>
                            <option value="Europe/Paris">Paris</option>
                            <option value="Asia/Tokyo">Tokyo</option>
                            <option value="Asia/Shanghai">Shanghai</option>
                            <option value="Asia/Kolkata">India</option>
                          </select>
                        </div>
                        
                        {settings.scheduled_visibility.rules.map((rule, index) => (
                          <div key={rule.id} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <input
                                type="text"
                                value={rule.name}
                                onChange={(e) => {
                                  const newRules = [...settings.scheduled_visibility.rules];
                                  newRules[index].name = e.target.value;
                                  handleSettingChange('scheduled_visibility', {
                                    ...settings.scheduled_visibility,
                                    rules: newRules
                                  });
                                }}
                                className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                                placeholder="Rule name"
                              />
                              <button
                                onClick={() => {
                                  const newRules = settings.scheduled_visibility.rules.filter(r => r.id !== rule.id);
                                  handleSettingChange('scheduled_visibility', {
                                    ...settings.scheduled_visibility,
                                    rules: newRules
                                  });
                                }}
                                className="ml-2 text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <label className="block text-xs text-gray-600 dark:text-gray-400">Start Time</label>
                                <input
                                  type="time"
                                  value={rule.start_time}
                                  onChange={(e) => {
                                    const newRules = [...settings.scheduled_visibility.rules];
                                    newRules[index].start_time = e.target.value;
                                    handleSettingChange('scheduled_visibility', {
                                      ...settings.scheduled_visibility,
                                      rules: newRules
                                    });
                                  }}
                                  className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-600"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-600 dark:text-gray-400">End Time</label>
                                <input
                                  type="time"
                                  value={rule.end_time}
                                  onChange={(e) => {
                                    const newRules = [...settings.scheduled_visibility.rules];
                                    newRules[index].end_time = e.target.value;
                                    handleSettingChange('scheduled_visibility', {
                                      ...settings.scheduled_visibility,
                                      rules: newRules
                                    });
                                  }}
                                  className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-600"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        <button
                          onClick={() => {
                            const newRule = {
                              id: Date.now(),
                              name: 'New Rule',
                              enabled: true,
                              days: [1, 2, 3, 4, 5],
                              start_time: '09:00',
                              end_time: '18:00',
                              action: 'show'
                            };
                            handleSettingChange('scheduled_visibility', {
                              ...settings.scheduled_visibility,
                              rules: [...settings.scheduled_visibility.rules, newRule]
                            });
                          }}
                          className="w-full px-3 py-2 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
                        >
                          + Add Time Rule
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Advanced Targeting */}
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-md font-medium text-gray-900 dark:text-white">Advanced Targeting</h3>
                      <input
                        type="checkbox"
                        id="advanced_targeting_enabled"
                        checked={settings.advanced_targeting.enabled}
                        onChange={(e) => handleSettingChange('advanced_targeting', {
                          ...settings.advanced_targeting,
                          enabled: e.target.checked
                        })}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </div>
                    
                    {settings.advanced_targeting.enabled && (
                      <div className="space-y-3 pl-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            User Roles
                          </label>
                          <select
                            multiple
                            value={settings.advanced_targeting.user_roles}
                            onChange={(e) => {
                              const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                              handleSettingChange('advanced_targeting', {
                                ...settings.advanced_targeting,
                                user_roles: selectedOptions
                              });
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            <option value="all">All Users</option>
                            <option value="guest">Guest Users</option>
                            <option value="user">Registered Users</option>
                            <option value="admin">Administrators</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Minimum Time on Page (seconds)
                          </label>
                          <input
                            type="number"
                            value={settings.advanced_targeting.user_behavior.min_time_on_page}
                            onChange={(e) => handleSettingChange('advanced_targeting', {
                              ...settings.advanced_targeting,
                              user_behavior: {
                                ...settings.advanced_targeting.user_behavior,
                                min_time_on_page: parseInt(e.target.value) || 0
                              }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            min="0"
                            step="1"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Performance Settings */}
                  <div className="border-t pt-4">
                    <h3 className="text-md font-medium text-gray-900 dark:text-white mb-3">Performance & Animation</h3>
                    <div className="space-y-3 pl-4">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id="lazy_loading"
                          checked={settings.performance_settings.lazy_loading}
                          onChange={(e) => handleSettingChange('performance_settings', {
                            ...settings.performance_settings,
                            lazy_loading: e.target.checked
                          })}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label htmlFor="lazy_loading" className="text-sm text-gray-700 dark:text-gray-300">
                          Lazy Loading
                        </label>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id="hover_effects"
                          checked={settings.performance_settings.hover_effects}
                          onChange={(e) => handleSettingChange('performance_settings', {
                            ...settings.performance_settings,
                            hover_effects: e.target.checked
                          })}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label htmlFor="hover_effects" className="text-sm text-gray-700 dark:text-gray-300">
                          Hover Effects
                        </label>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Animation Duration (ms)
                        </label>
                        <input
                          type="number"
                          value={settings.performance_settings.animation_duration}
                          onChange={(e) => handleSettingChange('performance_settings', {
                            ...settings.performance_settings,
                            animation_duration: parseInt(e.target.value) || 300
                          })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          min="100"
                          max="1000"
                          step="50"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Analytics Settings */}
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-md font-medium text-gray-900 dark:text-white">Analytics & Tracking</h3>
                      <input
                        type="checkbox"
                        id="analytics_enabled"
                        checked={settings.analytics.enabled}
                        onChange={(e) => handleSettingChange('analytics', {
                          ...settings.analytics,
                          enabled: e.target.checked
                        })}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </div>
                    
                    {settings.analytics.enabled && (
                      <div className="space-y-3 pl-4">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id="track_clicks"
                            checked={settings.analytics.track_clicks}
                            onChange={(e) => handleSettingChange('analytics', {
                              ...settings.analytics,
                              track_clicks: e.target.checked
                            })}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label htmlFor="track_clicks" className="text-sm text-gray-700 dark:text-gray-300">
                            Track Button Clicks
                          </label>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id="track_views"
                            checked={settings.analytics.track_views}
                            onChange={(e) => handleSettingChange('analytics', {
                              ...settings.analytics,
                              track_views: e.target.checked
                            })}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label htmlFor="track_views" className="text-sm text-gray-700 dark:text-gray-300">
                            Track Button Views
                          </label>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id="heatmap_tracking"
                            checked={settings.analytics.heatmap_tracking}
                            onChange={(e) => handleSettingChange('analytics', {
                              ...settings.analytics,
                              heatmap_tracking: e.target.checked
                            })}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label htmlFor="heatmap_tracking" className="text-sm text-gray-700 dark:text-gray-300">
                            Heatmap Tracking
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* A/B Testing */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2 text-purple-600" />
                  A/B Testing
                </h2>
                <button
                  onClick={() => setShowABTesting(!showABTesting)}
                  className="text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300"
                >
                  {showABTesting ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
              </div>
              
              {showABTesting && (
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="ab_testing_enabled"
                      checked={settings.ab_testing.enabled}
                      onChange={(e) => handleSettingChange('ab_testing', {
                        ...settings.ab_testing,
                        enabled: e.target.checked
                      })}
                      className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <label htmlFor="ab_testing_enabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Enable A/B Testing
                    </label>
                  </div>
                  
                  {settings.ab_testing.enabled && (
                    <div className="space-y-4">
                      {settings.ab_testing.tests.map((test, testIndex) => (
                        <div key={test.id} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-900 dark:text-white">{test.name}</h4>
                            <select
                              value={test.status}
                              onChange={(e) => {
                                const newTests = [...settings.ab_testing.tests];
                                newTests[testIndex].status = e.target.value;
                                handleSettingChange('ab_testing', {
                                  ...settings.ab_testing,
                                  tests: newTests
                                });
                              }}
                              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600"
                            >
                              <option value="draft">Draft</option>
                              <option value="running">Running</option>
                              <option value="paused">Paused</option>
                              <option value="completed">Completed</option>
                            </select>
                          </div>
                          
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{test.description}</p>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {test.variants.map((variant, variantIndex) => (
                              <div key={variant.id} className="p-3 bg-white dark:bg-gray-600 rounded border">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {variant.name}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {variant.traffic_percentage}% traffic
                                  </span>
                                </div>
                                
                                <div className="space-y-2 text-xs">
                                  <div className="flex justify-between">
                                    <span>Position:</span>
                                    <span className="font-medium">{variant.settings.position}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>WhatsApp Color:</span>
                                    <div className="flex items-center space-x-2">
                                      <div 
                                        className="w-4 h-4 rounded border"
                                        style={{ backgroundColor: variant.settings.whatsapp_button_color }}
                                      />
                                      <span>{variant.settings.whatsapp_button_color}</span>
                                    </div>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>AI Color:</span>
                                    <div className="flex items-center space-x-2">
                                      <div 
                                        className="w-4 h-4 rounded border"
                                        style={{ backgroundColor: variant.settings.ai_button_color }}
                                      />
                                      <span>{variant.settings.ai_button_color}</span>
                                    </div>
                                  </div>
                                </div>
                                
                                {test.status === 'running' && test.results[variant.id] && (
                                  <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-500">
                                    <div className="grid grid-cols-3 gap-2 text-xs">
                                      <div className="text-center">
                                        <div className="font-medium text-blue-600">{test.results[variant.id].impressions}</div>
                                        <div className="text-gray-500">Impressions</div>
                                      </div>
                                      <div className="text-center">
                                        <div className="font-medium text-green-600">{test.results[variant.id].clicks}</div>
                                        <div className="text-gray-500">Clicks</div>
                                      </div>
                                      <div className="text-center">
                                        <div className="font-medium text-purple-600">{test.results[variant.id].conversions}</div>
                                        <div className="text-gray-500">Conversions</div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                          
                          <div className="mt-3 flex items-center justify-between">
                            <div className="text-xs text-gray-500">
                              Metrics: {test.metrics.join(', ')}
                            </div>
                            <button
                              onClick={() => {
                                const newTests = settings.ab_testing.tests.filter(t => t.id !== test.id);
                                handleSettingChange('ab_testing', {
                                  ...settings.ab_testing,
                                  tests: newTests
                                });
                              }}
                              className="text-xs text-red-600 hover:text-red-700"
                            >
                              Delete Test
                            </button>
                          </div>
                        </div>
                      ))}
                      
                      <button
                        onClick={() => {
                          const newTest = {
                            id: Date.now(),
                            name: 'New A/B Test',
                            description: 'Test description',
                            status: 'draft',
                            variants: [
                              {
                                id: 'control',
                                name: 'Control',
                                settings: {
                                  position: 'bottom-right',
                                  whatsapp_button_color: '#25D366',
                                  ai_button_color: '#667eea'
                                },
                                traffic_percentage: 50
                              },
                              {
                                id: 'variant_a',
                                name: 'Variant A',
                                settings: {
                                  position: 'top-right',
                                  whatsapp_button_color: '#FF6B6B',
                                  ai_button_color: '#4ECDC4'
                                },
                                traffic_percentage: 50
                              }
                            ],
                            metrics: ['click_rate', 'conversion_rate'],
                            start_date: null,
                            end_date: null,
                            results: {
                              control: { impressions: 0, clicks: 0, conversions: 0 },
                              variant_a: { impressions: 0, clicks: 0, conversions: 0 }
                            }
                          };
                          handleSettingChange('ab_testing', {
                            ...settings.ab_testing,
                            tests: [...settings.ab_testing.tests, newTest]
                          });
                        }}
                        className="w-full px-4 py-2 text-sm text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20"
                      >
                        + Create New A/B Test
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Performance Monitoring */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <Activity className="w-5 h-5 mr-2 text-green-600" />
                  Performance Monitoring
                </h2>
                <button
                  onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                  className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                >
                  {showAdvancedSettings ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
              </div>
              
              {showAdvancedSettings && (
                <div className="space-y-4">
                  {/* Real-time Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-800 dark:text-green-200">Response Time</p>
                          <p className="text-xl sm:text-2xl font-bold text-green-600">24ms</p>
                        </div>
                        <div className="p-2 bg-green-100 dark:bg-green-800 rounded-full">
                          <Zap className="w-5 h-5 text-green-600" />
                        </div>
                      </div>
                      <p className="text-xs text-green-600 mt-1">Last 5 minutes</p>
                    </div>
                    
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Uptime</p>
                          <p className="text-xl sm:text-2xl font-bold text-blue-600">99.9%</p>
                        </div>
                        <div className="p-2 bg-blue-100 dark:bg-blue-800 rounded-full">
                          <CheckCircle className="w-5 h-5 text-blue-600" />
                        </div>
                      </div>
                      <p className="text-xs text-blue-600 mt-1">Last 30 days</p>
                    </div>
                    
                    <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-purple-800 dark:text-purple-200">Active Users</p>
                          <p className="text-xl sm:text-2xl font-bold text-purple-600">1.2K</p>
                        </div>
                        <div className="p-2 bg-purple-100 dark:bg-purple-800 rounded-full">
                          <Users className="w-5 h-5 text-purple-600" />
                        </div>
                      </div>
                      <p className="text-xs text-purple-600 mt-1">Current session</p>
                    </div>
                  </div>
                  
                  {/* Health Checks */}
                  <div className="border-t pt-4">
                    <h3 className="text-md font-medium text-gray-900 dark:text-white mb-3">System Health</h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded">
                        <span className="text-sm text-gray-700 dark:text-gray-300">Database Connection</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-xs text-green-600">Healthy</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded">
                        <span className="text-sm text-gray-700 dark:text-gray-300">API Endpoints</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-xs text-green-600">All OK</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded">
                        <span className="text-sm text-gray-700 dark:text-gray-300">Memory Usage</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                          <span className="text-xs text-yellow-600">75%</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded">
                        <span className="text-sm text-gray-700 dark:text-gray-300">Cache Status</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-xs text-green-600">Optimal</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Performance Actions */}
                  <div className="border-t pt-4">
                    <h3 className="text-md font-medium text-gray-900 dark:text-white mb-3">Quick Actions</h3>
                    <div className="flex flex-wrap gap-2">
                      <button className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                        Clear Cache
                      </button>
                      <button className="px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                        Optimize Database
                      </button>
                      <button className="px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                        Generate Report
                      </button>
                      <button className="px-3 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors">
                        Backup Settings
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
                Quick Stats
              </h2>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Total Pages</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {Object.keys(settings.page_visibility).length}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Visible Pages</span>
                  <span className="text-sm font-medium text-green-600">
                    {Object.values(settings.page_visibility).filter(v => v).length}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Hidden Pages</span>
                  <span className="text-sm font-medium text-red-600">
                    {Object.values(settings.page_visibility).filter(v => !v).length}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">WhatsApp Button</span>
                  <span className={`text-sm font-medium ${settings.whatsapp_enabled ? 'text-green-600' : 'text-red-600'}`}>
                    {settings.whatsapp_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">AI Chat Button</span>
                  <span className={`text-sm font-medium ${settings.ai_chat_enabled ? 'text-green-600' : 'text-red-600'}`}>
                    {settings.ai_chat_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>

            {/* Machine Learning Optimization */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <Brain className="w-5 h-5 mr-2 text-indigo-600" />
                  AI Optimization
                </h2>
                <button
                  onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                  className="text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
                >
                  {showAdvancedSettings ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
              </div>
              
              {showAdvancedSettings && (
                <div className="space-y-4">
                  {/* AI Recommendations */}
                  <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-indigo-100 dark:bg-indigo-800 rounded-full">
                        <Brain className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-indigo-800 dark:text-indigo-200 mb-2">AI Recommendation</h4>
                        <p className="text-sm text-indigo-700 dark:text-indigo-300 mb-3">
                          Based on user behavior analysis, we recommend moving the WhatsApp button to the top-right position 
                          during business hours for better engagement.
                        </p>
                        <div className="flex space-x-2">
                          <button className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors">
                            Apply Recommendation
                          </button>
                          <button className="px-3 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors">
                            Dismiss
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Auto-optimization Settings */}
                  <div className="border-t pt-4">
                    <h3 className="text-md font-medium text-gray-900 dark:text-white mb-3">Auto-optimization</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Enable AI Auto-optimization
                          </label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Automatically adjust settings based on performance data
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500"
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Smart A/B Testing
                          </label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Automatically create and run A/B tests
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500"
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Predictive Analytics
                          </label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Predict user behavior and optimize accordingly
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Learning Progress */}
                  <div className="border-t pt-4">
                    <h3 className="text-md font-medium text-gray-900 dark:text-white mb-3">Learning Progress</h3>
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                          <span>Model Accuracy</span>
                          <span>87%</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div className="bg-indigo-600 h-2 rounded-full" style={{ width: '87%' }}></div>
                        </div>
                      </div>
                      
                      <div>
                        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                          <span>Data Points Collected</span>
                          <span>12.5K</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div className="bg-green-600 h-2 rounded-full" style={{ width: '75%' }}></div>
                        </div>
                      </div>
                      
                      <div>
                        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                          <span>Optimization Cycles</span>
                          <span>24</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div className="bg-purple-600 h-2 rounded-full" style={{ width: '60%' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* AI Actions */}
                  <div className="border-t pt-4">
                    <h3 className="text-md font-medium text-gray-900 dark:text-white mb-3">AI Actions</h3>
                    <div className="flex flex-wrap gap-2">
                      <button className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                        Retrain Model
                      </button>
                      <button className="px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                        Generate Insights
                      </button>
                      <button className="px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                        Export Data
                      </button>
                      <button className="px-3 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors">
                        Reset Learning
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloatingChatManagement;
